/**
 * Your own account, for somebody who works here.
 *
 * ── WHY THIS IS NOT `/account` ──────────────────────────────────────────────
 *
 * `/account` is the CUSTOMER'S screen, and the sidebar used to send staff to
 * it. Christian: "this looks like the account for all users ... can we have a
 * different account route for the admin of the companies".
 *
 * It is not a styling complaint. That screen offers a default SHIPPING ADDRESS,
 * marketing preferences, "View orders" meaning the things you have bought, and
 * a request to delete your account. Guy has never bought anything from Obsessed
 * and deleting his account would take the studio's administrator with it. Every
 * one of those controls is either meaningless or dangerous for a staff member,
 * and a screen full of controls that do not apply teaches people that this
 * software is not about them.
 *
 * ── WHAT IS HERE, AND WHY SO LITTLE ─────────────────────────────────────────
 *
 * Only things that WORK. The customer form's "Save changes" makes no request:
 * it sets a flag, shows "Saved", and clears it after 2.6 seconds. Copying that
 * shape here would mean a staff member typing their phone number, being told it
 * was saved, and finding it gone tomorrow.
 *
 * So this screen states who you are, shows what your role actually permits,
 * sends you a link if you want to change your password, and signs you out. Four
 * things, each of which does what it says. A name field goes on it the day
 * there is somewhere to put the name.
 */
import { useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { ShieldCheck } from '@phosphor-icons/react';

import { Button, Callout } from '@/components/ui';
import { resetIdentity } from '@/query-client';
import { adminShellQuery } from '@/queries';

/** What the matrix calls each action, in the order somebody reads them. */
const ACTIONS = ['view', 'create', 'edit', 'delete'] as const;

export default function AdminAccountPage() {
  const { me, can } = useSuspenseQuery(adminShellQuery).data;
  const router = useRouter();
  const navigate = useNavigate();

  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = me.user?.email ?? '';

  const modules = Object.entries(can)
    .filter(([, actions]) => (actions ?? []).length > 0)
    .sort(([a], [b]) => a.localeCompare(b));

  async function sendResetLink() {
    setError(null);
    setSending(true);
    try {
      const res = await fetch('/api/password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        setError(body?.error?.message ?? 'That did not go through. Try again.');
        return;
      }
      setSent(true);
    } catch {
      setError('That did not go through. Check your connection.');
    } finally {
      setSending(false);
    }
  }

  async function signOut() {
    await fetch('/api/session', { method: 'DELETE', credentials: 'same-origin' });
    // The cache still holds this person's screens and their admin role until
    // it is dropped. In a studio with one shared laptop that is the next
    // person seeing the last one's work.
    resetIdentity();
    await router.invalidate();
    await navigate({ to: '/auth/sign-in' });
  }

  return (
    <div className="max-w-[720px]">
      <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Your account</h1>
      <p className="mt-1 text-[12.5px] text-muted">
        Who you are signed in as, and what that lets you do.
      </p>

      <div className="mt-5 rounded-[10px] border border-line bg-panel p-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[color:var(--color-control)] font-[family-name:var(--font-mono)] text-[13px] font-medium text-[color:var(--color-on-control)]">
            {(email || '?').slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold">{email || 'Signed out'}</p>
            <p className="flex items-center gap-1.5 text-[12.5px] text-muted">
              <ShieldCheck size={14} weight="bold" aria-hidden />
              {me.user?.role ?? (me.isAdmin ? 'administrator' : 'staff')}
            </p>
          </div>
        </div>
      </div>

      {/*
        * WHAT YOU MAY DO, read from the same place the navigation reads it.
        *
        * FR-809: a person is allowed to know what they may do. It also answers
        * the question somebody actually asks, which is "why can Katherine see
        * this column and I cannot", without them having to ask an administrator
        * to open the permissions matrix.
        */}
      <h2 className="mt-8 font-[family-name:var(--font-mono)] text-[10px] font-medium uppercase tracking-[0.08em] text-muted">
        What you can do
      </h2>
      {modules.length === 0 ? (
        <p className="mt-2 text-[13px] text-muted">
          Nothing yet. An administrator grants this on the Permissions screen.
        </p>
      ) : (
        <div className="mt-2 overflow-hidden rounded-[10px] border border-line bg-panel">
          <table className="dt w-full text-left">
            <thead>
              <tr>
                <th scope="col">Module</th>
                {ACTIONS.map((a) => <th key={a} scope="col">{a}</th>)}
              </tr>
            </thead>
            <tbody>
              {modules.map(([module, actions]) => (
                <tr key={module}>
                  <td className="text-[13px] font-medium">{module.replace(/_/g, ' ')}</td>
                  {ACTIONS.map((a) => (
                    <td key={a} className="text-[13px]">
                      {(actions ?? []).includes(a)
                        ? <span aria-label="yes">Yes</span>
                        : <span className="text-faint" aria-label="no">&mdash;</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="mt-8 font-[family-name:var(--font-mono)] text-[10px] font-medium uppercase tracking-[0.08em] text-muted">
        Password
      </h2>
      {sent ? (
        <Callout tone="ok" title="Check your email" className="mt-2">
          A link to set a new password is on its way to {email}. It is good for
          one use.
        </Callout>
      ) : (
        <>
          <p className="mt-2 text-[13px] text-muted">
            We email you a link rather than asking for your old password here,
            so a password is never typed into a screen somebody could be reading
            over your shoulder.
          </p>
          {error && <Callout tone="danger" title={error} className="mt-2" />}
          <div className="mt-3">
            <Button weight="secondary" onClick={sendResetLink} disabled={sending || !email}>
              {sending ? 'Sending' : 'Email me a link to change it'}
            </Button>
          </div>
        </>
      )}

      <h2 className="mt-8 font-[family-name:var(--font-mono)] text-[10px] font-medium uppercase tracking-[0.08em] text-muted">
        Signing out
      </h2>
      <p className="mt-2 text-[13px] text-muted">
        This ends the session on this computer and clears what was loaded for
        you. Worth doing on a shared machine.
      </p>
      <div className="mt-3">
        <Button weight="secondary" onClick={signOut}>Sign out</Button>
      </div>
    </div>
  );
}
